import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';

const useDietRequestColumns = ({
  countries,
  userInfo,
  setDietModalData,
  setShowDietModal,
  setReportsModalData,
  setShowReportsModal
}) => {
  // Helper function to safely render any value for debugging
  const safeRender = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      if (Array.isArray(value) && value.length === 0) return '';
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Helper function to format values for display
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return '';

    // Date formatting with browser locale
    if (key === 'recommendationDate' && typeof value === 'number') {
      return new Date(value).toLocaleDateString(navigator.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // Boolean formatting
    if (key === 'test') {
      return value === 0 || value === false ? 'No' : 'Yes';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  // Helper function to handle pluralization
  const pluralize = (count, singular) => {
    // Special case for diets
    if (singular === 'diets') {
      if (count === 0) return 'no diets';
      if (count === 1) return '1 diet';
      return `${count} diets`;
    }

    if (count === 1) {
      return `1 ${singular}`;
    }

    // Handle words that are already plural or have special rules
    const pluralForms = {
      'diet': 'diets',
      'diets': 'diets',
      'message': 'messages',
      'messages': 'messages',
      'forage': 'forages',
      'forages': 'forages',
      'report': 'reports',
      'reports': 'reports',
      'label': 'labels',
      'labels': 'labels',
      'goal': 'goals',
      'goals': 'goals',
      'problem': 'problems',
      'problems': 'problems',
      'feed': 'feeds',
      'feeds': 'feeds'
    };

    const plural = pluralForms[singular] || `${singular}s`;
    return `${count} ${plural}`;
  };

  // Helper function to truncate URLs and create links
  const formatUrlValue = (value, maxLength = 40) => {
    if (!value || typeof value !== 'string') return value;

    // Check if it's a URL
    const isUrl = value.startsWith('http') || value.startsWith('www') || value.includes('://');

    if (isUrl) {
      const truncated = value.length > maxLength ? `${value.substring(0, maxLength)}...` : value;
      return { isUrl: true, original: value, display: truncated };
    }

    return value;
  };

  // Helper function to get country name from country code
  const getCountryName = (countryCode) => {
    if (!countryCode || !countries || !Array.isArray(countries)) return countryCode || '';
    const country = countries.find(c => c.key === countryCode);
    return country ? country.value : countryCode;
  };

  // Column helper for TanStack Table
  const columnHelper = createColumnHelper();

  // Define admin-only column ids
  const adminOnlyColumns = [
    'test',
    'productMatrixVersion',
    'settingsVersion',
    'systemVersion',
    'recommendationId',
    'horseGoalsCount',
    'horseProblemsCount',
    // 'logId', // Hidden for now
    'profileName',
    'analyticsHost',
    'analyticsReferrer',
    'analyticsIpAddress',
    'analyticsCountry',
    'analyticsCity',
    'analyticsBrowser',
    'analyticsBrowserVersion',
    'analyticsOs',
    'analyticsOsVersion',
    'analyticsUserAgent'
  ];

  // Check if user is admin
  const isAdmin = userInfo?.role === 'admin';

  // Memoized columns configuration
  const allColumns = useMemo(() => [
    columnHelper.accessor(row => row.recommendation?.recommendationDate, {
      id: 'recommendationDate',
      header: 'DATE',
      cell: info => safeRender(formatValue('recommendationDate', info.getValue()), 'date', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.horse?.name, {
      id: 'horseName',
      header: 'HORSE NAME',
      cell: info => safeRender(info.getValue(), 'horseName', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.diets, {
      id: 'diets',
      header: 'RESULT',
      cell: info => {
        const diets = info.getValue();
        const rowData = info.row.original;
        const messages = rowData.recommendation?.messages || [];

        const dietCount = Array.isArray(diets) ? diets.length : 0;

        // Check message types
        const hasError = Array.isArray(messages) && messages.some(msg =>
          typeof msg === 'object' && msg.type === 'ERROR'
        );
        const hasWarning = Array.isArray(messages) && messages.some(msg =>
          typeof msg === 'object' && msg.type === 'WARNING'
        );

        // Determine badge color and text
        let badgeClasses;
        let displayText = pluralize(dietCount, 'diets');

        if (dietCount === 0) {
          badgeClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100";
        } else if (hasWarning && !hasError) {
          badgeClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100";
          displayText = `${displayText} with warning`;
        } else {
          badgeClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100";
        }

        return (
          <div className="text-left">
            <span
              className={`${badgeClasses} cursor-pointer hover:opacity-80 transition-opacity underline underline-offset-4`}
              onClick={() => {
                setDietModalData({
                  diets,
                  dietCount,
                  messages,
                  hasWarning,
                  hasError,
                  recommendation: rowData.recommendation,
                  // Include data from the same level as recommendation
                  analysisUrl: rowData?.analysisUrl,
                  siteBaseUrlPrefix: rowData?.siteBaseUrlPrefix,
                  siteBaseUrlSuffix: rowData?.siteBaseUrlSuffix,
                  reports: rowData?.recommendation?.report // Include reports for the diet modal
                });
                setShowDietModal(true);
              }}
            >
              {displayText}
            </span>
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.userForm, {
      id: 'user',
      header: 'USER',
      cell: info => {
        const userForm = info.getValue();
        const rowData = info.row.original;

        // Helper function to create user badge
        const createUserBadge = (name, email, key) => {
          if (email) {
            return (
              <div key={key} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200 mr-1 mb-1">
                <span className="mr-1">{name || 'User'}</span>
                <span>&lt;</span>
                <a
                  href={`mailto:${email}`}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4"
                  title={`Send email to ${email}`}
                >
                  {email}
                </a>
                <span>&gt;</span>
              </div>
            );
          }

          if (name) {
            return (
              <div key={key} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200 mr-1 mb-1">
                {name}
              </div>
            );
          }

          return null;
        };

        // Try to get user from userForm first
        if (userForm) {
          const firstName = userForm.firstName?.trim() || '';
          const lastName = userForm.lastName?.trim() || '';
          const email = userForm.email?.trim() || '';

          const nameParts = [];
          if (firstName) nameParts.push(firstName);
          if (lastName) nameParts.push(lastName);
          const fullName = nameParts.join(' ');

          const badge = createUserBadge(fullName, email, 'primary');
          if (badge) return badge;
        }

        // Fallback to reports data if userForm doesn't exist or is incomplete
        const reports = rowData.recommendation?.report;
        if (reports && Array.isArray(reports) && reports.length > 0) {
          const userBadges = [];
          const seenUsers = new Set();

          reports.forEach((report, index) => {
            if (report && typeof report === 'object') {
              const firstName = report.firstName?.trim() || '';
              const lastName = report.lastName?.trim() || '';
              const email = report.email?.trim() || '';

              const nameParts = [];
              if (firstName) nameParts.push(firstName);
              if (lastName) nameParts.push(lastName);
              const fullName = nameParts.join(' ');

              // Create unique identifier to avoid duplicate badges
              const userKey = `${fullName}-${email}`.toLowerCase();
              if (!seenUsers.has(userKey) && (fullName || email)) {
                seenUsers.add(userKey);
                const badge = createUserBadge(fullName, email, `report-${index}`);
                if (badge) userBadges.push(badge);
              }
            }
          });

          if (userBadges.length > 0) {
            return (
              <div className="flex flex-wrap">
                {userBadges}
              </div>
            );
          }
        }

        return '';
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.test, {
      id: 'test',
      header: 'IS TEST?',
      cell: info => safeRender(formatValue('test', info.getValue()), 'test', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.productMatrixVersion, {
      id: 'productMatrixVersion',
      header: 'PRODUCT MATRIX VERSION',
      cell: info => {
        const value = info.getValue();
        if (!value || value === 0 || value === '0' || value === '-' || value === 'n/a' || value === 'N/A') return '';
        return safeRender(value, 'productMatrixVersion', info.row.index);
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.settingsVersion, {
      id: 'settingsVersion',
      header: 'SETTINGS VERSION',
      cell: info => {
        const value = info.getValue();
        if (!value || value === 0 || value === '0' || value === '-' || value === 'n/a' || value === 'N/A') return '';
        return safeRender(value, 'settingsVersion', info.row.index);
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.systemVersion, {
      id: 'systemVersion',
      header: 'SYSTEM VERSION',
      cell: info => {
        const value = info.getValue();
        if (!value || value === 0 || value === '0' || value === '-' || value === 'n/a' || value === 'N/A') return '';
        return safeRender(value, 'systemVersion', info.row.index);
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.recommendationId, {
      id: 'recommendationId',
      header: 'REQUEST ID',
      cell: info => safeRender(info.getValue(), 'recommendationId', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    // columnHelper.accessor('logId', {
    //   header: 'LOG',
    //   cell: info => safeRender(info.getValue(), 'logId', info.row.index),
    //   enableColumnFilter: false,
    //   enableSorting: false,
    // }),
    columnHelper.accessor(row => row.recommendation?.report, {
      id: 'report',
      header: 'REPORTS SENT',
      cell: info => {
        const value = info.getValue();
        let count = 0;
        let reports = [];

        if (Array.isArray(value)) {
          count = value.length;
          reports = value;
        } else if (typeof value === 'number') {
          count = value;
        } else if (typeof value === 'string' && value.includes('report')) {
          const match = value.match(/(\d+)/);
          count = match ? parseInt(match[1]) : 0;
        }

        if (count === 0) return '';

        const displayText = count === 1 ? '1 report' : `${count} reports`;
        const badgeClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 cursor-pointer hover:opacity-80 transition-opacity underline underline-offset-4";

        return (
          <span
            className={badgeClasses}
            onClick={() => {
              setReportsModalData({
                reports: reports,
                reportCount: count,
                modalTitle: `Reports (${displayText})`
              });
              setShowReportsModal(true);
            }}
          >
            {displayText}
          </span>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.country, {
      id: 'country',
      header: 'COUNTRY',
      cell: info => safeRender(getCountryName(info.getValue()), 'country', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.profileName, {
      id: 'profileName',
      header: 'CLIENT',
      cell: info => safeRender(info.getValue(), 'profileName', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.horse?.sex, {
      id: 'horseSex',
      header: 'SEX',
      cell: info => safeRender(info.getValue(), 'horseSex', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.horse?.activityLevel, {
      id: 'horseActivityLevel',
      header: 'ACTIVITY LEVEL',
      cell: info => safeRender(info.getValue(), 'horseActivityLevel', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.horse?.horseDiscipline || '', {
      id: 'horseHorseDiscipline',
      header: 'DISCIPLINE',
      cell: info => safeRender(info.getValue(), 'horseHorseDiscipline', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const horse = row.recommendation?.horse;
      if (!horse) return '';
      const weight = horse.weight;
      const unit = horse.weightUnit || 'kg';
      return weight ? `${weight} ${unit}` : '';
    }, {
      id: 'horseWeight',
      header: 'WEIGHT',
      cell: info => safeRender(info.getValue(), 'horseWeight', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.horse?.keeperQuality, {
      id: 'horseKeeperQuality',
      header: 'KEEPER QUALITY',
      cell: info => safeRender(info.getValue(), 'horseKeeperQuality', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const goals = row.recommendation?.horse?.goals;
      return Array.isArray(goals) ? goals.length : 0;
    }, {
      id: 'horseGoalsCount',
      header: 'GOALS',
      cell: info => safeRender(info.getValue(), 'horseGoalsCount', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const problems = row.recommendation?.horse?.problems;
      return Array.isArray(problems) ? problems.length : 0;
    }, {
      id: 'horseProblemsCount',
      header: 'PROBLEMS',
      cell: info => safeRender(info.getValue(), 'horseProblemsCount', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const pasture = row.recommendation?.pasture;
      if (!pasture || typeof pasture !== 'object') return pasture || '';
      const { time, overgrazed, quality, type } = pasture;
      const overGrazedText = overgrazed ? 'over grazed' : 'not over grazed';
      return `${time || ''} hours on ${overGrazedText} ${quality || ''} ${type || ''}`.trim();
    }, {
      id: 'pasture',
      header: 'PASTURE',
      cell: info => safeRender(info.getValue(), 'pasture', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const forages = row.recommendation?.forages;
      if (!forages || !Array.isArray(forages) || forages.length === 0) return '';

      // Filter out empty forages and format each one
      const formattedForages = forages
        .filter(forage => forage && (forage.id || forage.name || forage.quantity))
        .map(forage => {
          const name = forage?.name || `Forage ${forages.indexOf(forage) + 1}`;
          const quality = forage?.quality;
          const quantity = forage?.quantity;
          const unit = forage?.unit;

          let displayName = quality ? `${name} (${quality})` : name;

          if (quantity !== undefined && quantity !== null) {
            const formattedQuantity = typeof quantity === 'number' ?
              Number(quantity).toFixed(2) : quantity;
            const quantityText = unit ? `${formattedQuantity} ${unit}` : String(formattedQuantity);
            displayName = `${displayName}: ${quantityText}`;
          }

          return displayName;
        });

      return formattedForages.join(', ');
    }, {
      id: 'forages',
      header: 'FORAGES',
      cell: info => safeRender(info.getValue(), 'forages', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const temp = row.recommendation?.temperature;
      const humidity = row.recommendation?.humidity;
      if (temp && humidity) {
        return `${temp} [${humidity}]`;
      }
      return temp || humidity || '';
    }, {
      id: 'climate',
      header: 'CLIMATE',
      cell: info => {
        const value = info.getValue();
        if (!value) return '';

        // Parse temperature and humidity
        const tempMatch = value.match(/(\w+)/);
        const humidityMatch = value.match(/\[([^\]]+)\]/);

        const temperature = tempMatch ? tempMatch[1] : '';
        const humidity = humidityMatch ? humidityMatch[1] : '';

        // Temperature icons and colors
        const getTemperatureDisplay = (temp) => {
          const tempLower = temp.toLowerCase();
          if (tempLower.includes('hot') || tempLower.includes('warm')) {
            return (
              <div
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100"
                title={tempLower}
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C13.1 2 14 2.9 14 4V11.5C15.2 12.2 16 13.5 16 15C16 17.2 14.2 19 12 19S8 17.2 8 15C8 13.5 8.8 12.2 10 11.5V4C10 2.9 10.9 2 12 2M12 4V12L10.5 13C10.2 13.2 10 13.6 10 14C10 14.6 10.4 15 11 15H13C13.6 15 14 14.6 14 14C14 13.6 13.8 13.2 13.5 13L12 12V4Z"/>
                </svg>
                <span>{tempLower}</span>
              </div>
            );
          } else if (tempLower.includes('cold') || tempLower.includes('cool')) {
            return (
              <div
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100"
                title={tempLower}
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,2A7,7 0 0,1 19,9C19,11.38 17.81,13.47 16,14.74V17A1,1 0 0,1 15,18H9A1,1 0 0,1 8,17V14.74C6.19,13.47 5,11.38 5,9A7,7 0 0,1 12,2M12,4A5,5 0 0,0 7,9C7,10.64 7.81,12.09 9,13V16H15V13C16.19,12.09 17,10.64 17,9A5,5 0 0,0 12,4Z"/>
                </svg>
                <span>{tempLower}</span>
              </div>
            );
          } else {
            return (
              <div
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                title={tempLower}
              >
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C13.1 2 14 2.9 14 4V11.5C15.2 12.2 16 13.5 16 15C16 17.2 14.2 19 12 19S8 17.2 8 15C8 13.5 8.8 12.2 10 11.5V4C10 2.9 10.9 2 12 2M12 4V12L10.5 13C10.2 13.2 10 13.6 10 14C10 14.6 10.4 15 11 15H13C13.6 15 14 14.6 14 14C14 13.6 13.8 13.2 13.5 13L12 12V4Z"/>
                </svg>
                <span>{tempLower}</span>
              </div>
            );
          }
        };

        // Humidity display with icon
        const getHumidityDisplay = (hum) => {
          if (!hum) return null;

          // Clean up humidity text
          const cleanHumidity = hum.replace(/_/g, ' ').replace(/percent/g, '%');

          return (
            <div
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-800 dark:text-cyan-100 ml-1"
              title={cleanHumidity}
            >
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,2C15.86,2 19,5.13 19,9C19,14.25 15.25,18 12,18C8.75,18 5,14.25 5,9C5,5.13 8.14,2 12,2M12,4.36C9.72,4.36 7.86,6.22 7.86,8.5C7.86,12.41 12,16.64 12,16.64C12,16.64 16.14,12.41 16.14,8.5C16.14,6.22 14.28,4.36 12,4.36Z"/>
              </svg>
              <span>{cleanHumidity}</span>
            </div>
          );
        };

        if (temperature && humidity) {
          return (
            <div className="flex items-center">
              {getTemperatureDisplay(temperature)}
              {getHumidityDisplay(humidity)}
            </div>
          );
        } else if (temperature) {
          return getTemperatureDisplay(temperature);
        } else if (humidity) {
          return getHumidityDisplay(humidity);
        }

        return safeRender(value, 'climate', info.row.index);
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => {
      const feed = row.recommendation?.userSelectedFeed;
      if (!feed) return '';
      const name = feed.name;
      const id = feed.id;
      if (name && id) return `${name} (ID: ${id})`;
      if (name) return name;
      if (id) return `ID: ${id}`;
      return '';
    }, {
      id: 'userSelectedFeed',
      header: 'USER SELECTED FEED',
      cell: info => safeRender(info.getValue(), 'userSelectedFeed', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.host, {
      id: 'analyticsHost',
      header: 'HOST',
      cell: info => {
        const value = info.getValue();

        if (!value) return '';

        const urlData = formatUrlValue(value);
        if (urlData && typeof urlData === 'object' && urlData.isUrl) {
          return (
            <a
              href={urlData.original}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4"
              title={urlData.original}
            >
              {urlData.display}
            </a>
          );
        }
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        return String(value);
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.referrer, {
      id: 'analyticsReferrer',
      header: 'REFERRER',
      cell: info => {
        const value = info.getValue();

        if (!value) return '';

        const urlData = formatUrlValue(value);
        if (urlData && typeof urlData === 'object' && urlData.isUrl) {
          return (
            <a
              href={urlData.original}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4"
              title={urlData.original}
            >
              {urlData.display}
            </a>
          );
        }
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        return String(value);
      },
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.ipAddress, {
      id: 'analyticsIpAddress',
      header: 'IP ADDRESS',
      cell: info => safeRender(info.getValue(), 'analyticsIpAddress', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => getCountryName(row.recommendation?.analyticsData?.country), {
      id: 'analyticsCountry',
      header: 'ANALYTICS COUNTRY',
      cell: info => safeRender(info.getValue(), 'analyticsCountry', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.city, {
      id: 'analyticsCity',
      header: 'CITY',
      cell: info => safeRender(info.getValue(), 'analyticsCity', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.browser, {
      id: 'analyticsBrowser',
      header: 'BROWSER',
      cell: info => safeRender(info.getValue(), 'analyticsBrowser', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.browserVersion, {
      id: 'analyticsBrowserVersion',
      header: 'BROWSER VERSION',
      cell: info => safeRender(info.getValue(), 'analyticsBrowserVersion', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.os, {
      id: 'analyticsOs',
      header: 'OS',
      cell: info => safeRender(info.getValue(), 'analyticsOs', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.osVersion, {
      id: 'analyticsOsVersion',
      header: 'OS VERSION',
      cell: info => safeRender(info.getValue(), 'analyticsOsVersion', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor(row => row.recommendation?.analyticsData?.userAgent, {
      id: 'analyticsUserAgent',
      header: 'USER AGENT',
      cell: info => safeRender(info.getValue(), 'analyticsUserAgent', info.row.index),
      enableColumnFilter: false,
      enableSorting: false,
    }),
  ], [countries]);

  // Filter columns based on user role and data availability
  const columns = useMemo(() => {
    let columnsToFilter = allColumns;

    // First filter admin-only columns if user is not admin
    if (!isAdmin) {
      columnsToFilter = columnsToFilter.filter(column => {
        const columnId = column.accessorKey || column.id;
        return !adminOnlyColumns.includes(columnId);
      });
    }

    // Hide country column if there's only one country option
    if (countries && countries.length <= 1) {
      columnsToFilter = columnsToFilter.filter(column => {
        const columnId = column.accessorKey || column.id;
        return columnId !== 'country';
      });
    }

    return columnsToFilter;
  }, [allColumns, isAdmin, adminOnlyColumns, countries]);

  return {
    columns,
    formatValue,
    pluralize,
    formatUrlValue,
    getCountryName
  };
};

export default useDietRequestColumns;
